import { types, flow } from "mobx-state-tree";

import * as YoutubeAPI from "api/youtube";
import CONSTANTS from "const/constants";
import Persistence from "pages/common/lib/grabyo-persistence";

import { UserCredentialBaseStore } from "./user-credential-base-store";

const { CREDENTIAL_TYPE_LABELS, PLATFORM_TYPES, YOUTUBE_MONETIZATION } =
    CONSTANTS;

const model = {
    googleTitle: types.maybeNull(types.string),
    googleProfileImageUrl: types.maybeNull(types.string),
    googleEmail: types.maybeNull(types.string)
};

const volatile = (self) => {
    const __name__ = self.name;
    const __profileImage__ = self.profileImage;

    return {
        name: self?.googleTitle ?? __name__,
        profileImage: self?.googleProfileImageUrl ?? __profileImage__,
        typeLabel: CREDENTIAL_TYPE_LABELS.YOUTUBE,
        platformType: PLATFORM_TYPES.YOUTUBE,
        shareKey: `shareViaYoutubeCount`,
        monetizationEnabled: self.isMonetizationEnabled(),
        monetizationActive: false,
        youtubePolicyId: ``,
        youtubeContentOwnerId: ``
    };
};

const views = (self) => {
    return {
        isMonetizationEnabled() {
            // Did the user authorized Content ID API for this Channel?
            return self.contentIdApiEnabled === `true`;
        },

        isMonetizationActive() {
            // Is Content ID API currently active for this Channel?
            return self.monetizationActive === true;
        },

        getMonetizationShareInfo() {
            if (
                !self.monetizationActive ||
                self.youtubeContentOwnerId === YOUTUBE_MONETIZATION.NONE
            ) {
                return {};
            }

            return {
                youtubePolicyId: self.youtubePolicyId,
                youtubeContentOwnerId: self.youtubeContentOwnerId
            };
        }
    };
};

const actions = (self) => {
    const __onShareSuccessful__ = self.onShareSuccessful;

    return {
        afterCreate() {
            if (self.isMonetizationEnabled()) {
                self.loadPersistedSettings();
            }
        },

        onShareSuccessful(response) {
            return __onShareSuccessful__.call(self, self.shareKey, response);
        },

        persistData() {
            const monetizationActive =
                self.youtubeMonetizationFlag ===
                YOUTUBE_MONETIZATION.CONTENT_ID;
            self.monetizationActive = monetizationActive;

            const id = self.socialId;
            const socialCred = {
                id,
                monetizationActive,
                youtubePolicyId: self.youtubePolicyId,
                youtubeContentOwnerId: self.youtubeContentOwnerId
            };

            const persistedCred = Persistence.PermStore.get(
                `socialCredential:${id}`
            );
            const data = persistedCred
                ? { ...persistedCred, ...socialCred }
                : socialCred;

            Persistence.PermStore.set(`socialCredential:${id}`, data);
        },

        setMonetizationActive(active) {
            self.monetizationActive = active;
        },

        setYoutubePolicyId(id) {
            self.youtubePolicyId = id;
        },

        setYoutubeContentOwnerId(id) {
            self.youtubeContentOwnerId = id;
        }
    };
};

const asyncActions = (self) => {
    const __share__ = self.share;

    return {
        /**
         * Overrides the base share method to add youtube specific share data
         * @async
         * @param {String} shareId
         * @param {object} data
         * @return {ShareObject | Error}
         */
        share: flow(function* share(shareId, data) {
            return yield __share__.call(self, shareId, {
                ...data,
                ...self.getMonetizationShareInfo()
            });
        }),

        fetchPolicies: flow(function* fetchPolicies(contentOwnerId) {
            try {
                return yield YoutubeAPI.getYoutubePolicies(
                    self.userId,
                    self.socialId,
                    contentOwnerId
                );
            } catch (error) {
                console.error(error);
            }
        }),

        fetchContentOwners: flow(function* fetchContentOwners() {
            try {
                return yield YoutubeAPI.getYoutubeContentOwners(
                    self.userId,
                    self.socialId
                );
            } catch (error) {
                console.error(error);
            }
        }),

        disableContentIdApi: flow(function* disableContentIdApi() {
            try {
                const response = yield YoutubeAPI.deleteYoutubeContentOwners(
                    self.userId,
                    self.socialId
                );
                self.update(response);
            } catch (error) {
                console.error(error);
            }
        })
    };
};

const _UserCredentialYoutubeStore = types
    .model(`UserCredentialYoutube`, model)
    .volatile(volatile)
    .views(views)
    .actions(actions)
    .actions(asyncActions);

export const UserCredentialYoutubeStore = types
    .compose(UserCredentialBaseStore, _UserCredentialYoutubeStore)
    .named(`UserCredentialYoutubeStore`);
